import {BoundingRectAndOffsets, WixSdk} from '@wix/js-sdk-wrapper';
import {combineLatest, from, fromEvent, merge, Observable} from 'rxjs';
import {fromPromise} from 'rxjs/internal-compatibility';
import {debounceTime, distinctUntilChanged, map, share, startWith, switchMap} from 'rxjs/operators';
import {ComponentInfo, PageWithSubPagesInfo, StyleParams} from '../../../typings/sdk';
import {debug} from '../../utils/debug';
import {areObjectsEqual} from '../../utils/objects';
import {getPageId} from '../../utils/wixSdk/getPageId';

export const subscribe = <T>(eventName: string): Observable<T> => {
  return new Observable<T>((subscriber) => {
    const id = WixSdk.addEventListener(eventName, (event: T) => {
      subscriber.next(event);
    });
    return () => {
      WixSdk.removeEventListener(eventName, id);
    };
  }).pipe(share());
};

export const pageNavigation$ = subscribe<{isAppOnPage: boolean; wasAppOnPage: boolean}>(WixSdk.Events.PAGE_NAVIGATION);

export const styleParams$: Observable<StyleParams> = merge(
  fromPromise(WixSdk.Styles.getStyleParams()),
  subscribe<StyleParams>(WixSdk.Events.STYLE_PARAMS_CHANGE).pipe(
    debug('styleParams', 'new event'),
    debounceTime(100),
    debug('styleParams', 'after debounce event'),
  ),
).pipe(
  debug('styleParams', 'before distinct', true),
  distinctUntilChanged((curr, prev) => areObjectsEqual(curr, prev)),
  debug('styleParams', 'after distinct', true),
  share(),
);

export const componentInfo$ = from(WixSdk.getComponentInfo() as Promise<ComponentInfo>);
export const sitePages$ = from(WixSdk.getSitePages({}) as Promise<PageWithSubPagesInfo[]>);
export const currentPageId$ = from(WixSdk.getCurrentPageId() as Promise<string>);

export const rect$: Observable<BoundingRectAndOffsets> = fromEvent(window, 'resize').pipe(
  debug('rect', 'window has changed dimension'),
  debounceTime(150),
  startWith(null),
  switchMap(() => from(WixSdk.getBoundingRectAndOffsets())),
  distinctUntilChanged((curr, prev) => areObjectsEqual(curr, prev)),
  debug('rect', 'got rect'),
);

type SettingsUpdateEvent = {type: string; compId?: string; albumId?: string} | 'migrationEnd' | 'migrationStart';
export const settingUpdate$ = subscribe<SettingsUpdateEvent>(WixSdk.Events.SETTINGS_UPDATED).pipe(
  map((event) => (typeof event === 'string' ? {type: event} : event)),
);
export const pageId$ = combineLatest([componentInfo$, sitePages$, currentPageId$]).pipe(
  map(([componentInfo, sitePages, currentPageId]) => getPageId(componentInfo, sitePages, currentPageId)),
);
